import { safeLocalStorage } from '@/lib/safeStorage';
import {useEffect, useState} from 'react';
import {jwtDecode} from 'jwt-decode';
import {STORAGE_KEYS} from '../lib/constants';
import {useOrganization} from './useQueries';
import {useAuthStatus} from './useAuthStatus';
import {getUserDisplayName} from '@ruta/shared';

export interface UserInfo {
  id: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  role: string | null;
  orgId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
}

export function useUserInfo(): UserInfo & { loading: boolean } {
  const { isAuthenticated, orgId } = useAuthStatus();
  const [userInfo, setUserInfo] = useState<UserInfo>({
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    fullName: null,
    role: null,
    orgId: null,
    organizationName: null,
    organizationSlug: null,
  });
  const [loading, setLoading] = useState(false);

  // Use TanStack Query hook for organization data
  const { data: organizationData } = useOrganization({ enabled: isAuthenticated && !!orgId });

  useEffect(() => {
    if (!isAuthenticated) {
      setUserInfo({
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        fullName: null,
        role: null,
        orgId: null,
        organizationName: null,
        organizationSlug: null,
      });
      return;
    }

    setLoading(true);

    try {
      const token = safeLocalStorage.getItem(STORAGE_KEYS.ID_TOKEN);
      if (!token) {
        setLoading(false);
        return;
      }

      const decoded: any = jwtDecode(token);

      // Extract basic user info from token
      const id = decoded.sub;
      const email = decoded.email;
      const firstName = decoded.given_name;
      const lastName = decoded.family_name;
      const fullName = firstName && lastName
        ? `${firstName} ${lastName}`
        : getUserDisplayName(decoded);
      const role = decoded['custom:role'] || 'Admin';
      const orgId = decoded['custom:orgId'];

      // Use organization data from TanStack Query hook
      const organizationName = organizationData?.organization?.name || null;
      const organizationSlug = organizationData?.organization?.slug || null;

      setUserInfo({
        id,
        email,
        firstName,
        lastName,
        fullName,
        role,
        orgId,
        organizationName,
        organizationSlug,
      });

    } catch (error) {
      console.error('Failed to load user info:', error);
      setUserInfo({
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        fullName: 'User',
        role: 'Admin',
        orgId: null,
        organizationName: null,
        organizationSlug: null,
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, orgId, organizationData]);

  return { ...userInfo, loading };
}